import { find } from 'lodash';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, MessagePlugin, Space, Table } from 'tdesign-react';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { createComponent, updateComponent } from '@/services/templateService';
import { isEmpty, renderOptions } from '@/utils/common';
import CodeMirrorLight from '@/components/CodeMirrorLight';
import SvgIcon from '@/components/SvgIcon';
import Panel from '@/components/Panel';
import Descriptions from '@/components/Descriptions';
import DescriptionItem from '@/components/DescriptionItem';
import { fetchComponentDiff } from '@/slices/templateSlice';
import { clusterTypeOptions, variableOptionalOptions } from '@/utils/constant';
import { infos } from '..';

const Diff = React.lazy(() => import('./Diff'));

const getTitle = (index) => {
  const idx = index % 6;
  const tip = infos[idx];
  if (index < 6) {
    return (
      <>
        部署/升级执行<Sub>{tip}</Sub>
      </>
    );
  }
  if (index < 12) {
    return (
      <>
        回滚执行<Sub>{tip}</Sub>
      </>
    );
  }
  if (index < 18) {
    return (
      <>
        卸载执行<Sub>{tip}</Sub>
      </>
    );
  }
  if (index === 18) {
    return 'START/RESTART脚本';
  }
  if (index === 19) {
    return 'STOP脚本';
  }
  if (index === 20) {
    return '进程状态脚本';
  }
};

function ConfirmStep({ preStep, cache, fetchComponentDiff }) {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !isEmpty(id); // 是否编辑状态
  // 合并后的数据
  const merged = useMemo(() => {
    const merge = {};
    let scriptCreateBOList = [];
    Object.entries(cache).forEach(([, value]) => {
      Object.assign(merge, value);
      // 合并scriptCreateBOList
      if (value.scriptCreateBOList) {
        scriptCreateBOList = scriptCreateBOList.concat(value.scriptCreateBOList);
      }
    });
    merge.scriptCreateBOList = scriptCreateBOList.map((item) => ({
      ...item,
      scriptContent: item.scriptContent || '',
    }));
    return merge;
  }, [cache]);

  const params = useMemo(() => {
    const _params = {
      ...merged,
      ipVariables: JSON.stringify(merged.ipVariables || []),
      packageVariables: JSON.stringify(merged.packageVariables || []),
    };
    if (id) {
      _params.componentStencilId = id;
    }
    return _params;
  }, [merged, id]);

  const columns = [
    {
      colKey: 'variableType',
      title: '参数类型',
      width: 90,
      cell: renderOptions,
      options: variableOptionalOptions,
      fixed: 'left',
    },
    {
      colKey: 'key',
      title: '变量key',
      width: 200,
    },
    {
      colKey: 'keyName',
      title: '显示名称',
      width: 150,
    },
    {
      colKey: 'defaultValue',
      title: '默认值',
      width: 100,
    },
    {
      colKey: 'desc',
      title: '变量备注',
      width: 300,
    },
  ];

  const code = useMemo(() => {
    if (isEdit) {
      return (
        <Suspense fallback={<div />}>
          <Diff />
        </Suspense>
      );
    } else {
      return (
        visible && (
          <>
            {merged.scriptCreateBOList.map((item, index) => (
              <React.Fragment key={index}>
                <Descriptions.Panel title={getTitle(index)} />
                <CodeMirrorLight value={item?.scriptContent} readOnly />
              </React.Fragment>
            ))}
          </>
        )
      );
    }
  }, [isEdit, merged, visible]);

  useEffect(() => {
    if (isEdit) {
      fetchComponentDiff(params);
      setVisible(true);
    }
  }, [isEdit, fetchComponentDiff, params]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      if (isEdit) {
        await updateComponent(params);
      } else {
        // 新增
        await createComponent(params);
      }
      MessagePlugin.success('操作成功!');
      navigate('/maintenance/template/component');
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Root
      footer={
        <Space size='small'>
          <Button variant='outline' onClick={preStep}>
            上一步
          </Button>
          <Button theme='primary' loading={submitting} onClick={() => handleSubmit()}>
            提交
          </Button>
        </Space>
      }
    >
      <Descriptions title='基本信息' column={2}>
        <DescriptionItem label='组件名称' value={merged.name} />
        <DescriptionItem
          label='组件类型'
          value={find(clusterTypeOptions, { value: merged.type })?.label}
        />
        <DescriptionItem label='保留版本个数' value={merged.reservedVersionNum} />
        <DescriptionItem label='组件作用备注' value={merged.note} />
        <DescriptionItem label='部署目录' value={merged.deployDir} span={2} />
        <DescriptionItem label='存储目录' value={merged.repoDir} span={2} />
        <DescriptionItem label='需要替换的目录' value={merged.variableSubstitutionPath} span={2} />
      </Descriptions>

      <Descriptions.Panel style={{ marginTop: 12 }} title='组件包级别变量' />
      <Table
        style={{ marginTop: 8 }}
        maxHeight={1000}
        data={merged.packageVariables || []}
        columns={columns}
      />
      <Descriptions.Panel style={{ margin: '12px 0' }} title='IP级别变量' />
      <Table maxHeight={1000} data={merged.ipVariables || []} columns={columns} />

      {!isEdit && (
        <Title onClick={() => setVisible((vis) => !vis)}>
          脚本信息
          <Icon type='chevron-double-up' rotate={visible ? 0 : 180} />
        </Title>
      )}

      {code}
    </Root>
  );
}

export default connect(null, {
  fetchComponentDiff,
})(ConfirmStep);

const Root = styled(Panel)`
  margin-top: 16px;
  .descriptions-title {
    font-size: 14px;
    font-weight: bold;
  }
  .code-mirror-light {
    height: 300px;
  }
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: var(--td-brand-color-7);
  padding: 20px 0 6px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const Icon = styled(SvgIcon)`
  transition: 0.4s all cubic-bezier(0.645, 0.045, 0.355, 1);
  transform: rotate(${(props) => props.rotate}deg);
  margin-left: 4px;
`;

const Sub = styled.span`
  font-weight: 400;
  font-size: smaller;
  padding-left: 1em;
  color: var(--td-font-gray-2);
`;
